import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  FileText,
  Gauge,
  ListChecks,
  LogOut,
  Menu,
  PiggyBank,
  Receipt,
  Settings,
  ShieldAlert,
  Timer,
  Users,
  X,
} from 'lucide-react';
import { ADMIN_PERMISSIONS, invoicingHomePath } from '@codepass/shared';
import { create } from 'zustand';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui';

/**
 * Bredden hvor sidepanelet foldes ud som udgangspunkt. Tailwinds `xl`.
 *
 * Ikke `lg` (1024), som PRD par. 15.5 oprindeligt skrev: en iPad i landskab er praecis
 * 1024 px bred og ville dermed lande paa desktop-siden af graensen og staa udfoldet. Med
 * 1280 er alle almindelige tablet-bredder foldet sammen - iPad 1024, iPad Pro 11" 1194 -
 * mens baerbare fra 1280 og op er udfoldet.
 */
const SIDEBAR_EXPANDED_MIN_WIDTH = 1280;

/** Ét sted at stille spoergsmaalet, saa startvaerdi og lytteren nedenfor ikke kan afvige. */
const EXPANDED_QUERY = `(min-width: ${SIDEBAR_EXPANDED_MIN_WIDTH}px)`;

function prefersCollapsedSidebar(): boolean {
  // matchMedia findes ikke under server-rendering eller i en jsdom uden stub.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return !window.matchMedia(EXPANDED_QUERY).matches;
}

/** UI-tilstand i Zustand, server-tilstand i TanStack Query. Ingen overlap. */
interface UiState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** Saettes naar viewporten krydser graensen. Se useSidebarViewportDefault. */
  applyViewportDefault: (collapsed: boolean) => void;
  /** The menu as a drawer on a phone, below `md` - where the sidebar is hidden. */
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  sidebarCollapsed: prefersCollapsedSidebar(),
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  applyViewportDefault: (collapsed) => set({ sidebarCollapsed: collapsed }),
  mobileNavOpen: false,
  setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
}));

/**
 * Closes the phone menu when the page changes and on Escape. The links close it too, but a
 * route change from anywhere else - the bell, a link in the page - must not leave it open.
 */
function useMobileNavAutoClose(): void {
  const { pathname } = useLocation();
  const { mobileNavOpen, setMobileNavOpen } = useUiStore();

  useEffect(() => setMobileNavOpen(false), [pathname, setMobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen, setMobileNavOpen]);
}

/**
 * Holder udgangspunktet ved lige, naar viewporten skifter klasse.
 *
 * Startvaerdien i store'en laeses én gang ved indlaesning, og det er ikke nok: en tablet der
 * drejes fra portraet til landskab - eller et vindue der traekkes stoerre - skulle ellers
 * beholde det layout den startede med.
 *
 * `change` fyrer kun naar graensen faktisk krydses, ikke ved hver eneste pixel. Et manuelt
 * tryk paa knappen bliver derfor staaende, saa laenge man er inden for samme klasse, og
 * foerst naar man skifter mellem tablet og desktop vinder udgangspunktet igen.
 */
function useSidebarViewportDefault(): void {
  const applyViewportDefault = useUiStore((state) => state.applyViewportDefault);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia(EXPANDED_QUERY);
    const onChange = (event: MediaQueryListEvent): void => applyViewportDefault(!event.matches);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [applyViewportDefault]);
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  badge?: number;
}

interface AlertCounts {
  critical: number;
  warning: number;
  info: number;
}

function openCount(counts: AlertCounts | undefined): number {
  return (counts?.critical ?? 0) + (counts?.warning ?? 0) + (counts?.info ?? 0);
}

export function AppShell() {
  const { user, logout, can, canAny } = useAuth();
  /* Uden 'Se alle alarmer' er menupunktet og klokken det samme: ens egne. */
  const seesAllAlerts = can('alerts.view_all');
  const seesOwnAlerts = can('alerts.view_own');
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNavOpen } = useUiStore();

  useSidebarViewportDefault();
  useMobileNavAutoClose();

  /*
   * To indgange til /alarmer, og hver taeller det, den lander paa: menuen er overblikket over
   * alles alarmer, klokken er dine egne - se ALL_RECIPIENTS i pages/Alerts.tsx. Talte de det
   * samme, ville den ene love flere eller faerre, end siden bagved viser.
   *
   * Noeglerne er de samme, som /alarmer selv bruger, saa siden og skallen deler svaret.
   */
  const { data: allAlerts } = useQuery({
    queryKey: ['alerts', 'summary', 'all'],
    queryFn: () => api.get<AlertCounts>('/alerts/summary'),
    refetchInterval: 60_000,
    enabled: seesAllAlerts,
  });

  const { data: myAlerts } = useQuery({
    queryKey: ['alerts', 'summary', user?.id ?? 'all'],
    queryFn: () => api.get<AlertCounts>('/alerts/summary', { recipient_id: user?.id }),
    refetchInterval: 60_000,
    enabled: seesOwnAlerts,
  });

  const myOpenAlerts = openCount(myAlerts);
  /* Fakturering aabner paa den foerste fane, brugeren har - se `invoicingHomePath`. */
  const invoicing = invoicingHomePath(user);

  /** Kun det, rettighederne aabner - se PERMISSIONS i @codepass/shared. */
  const items: NavItem[] = [
    can('dashboard.view') && { to: '/dashboard', label: 'Dashboard', icon: Gauge },
    canAny('contracts.view', 'service_agreements.view') && {
      /* Kun serviceaftalerne: saa er det dén fane, menupunktet aabner. */
      to: can('contracts.view') ? '/kontrakter' : '/kontrakter?kind=service',
      label: 'Kontrakter',
      icon: FileText,
    },
    can('hours.view') && { to: '/timer', label: 'Timer', icon: Timer },
    invoicing && { to: invoicing, label: 'Fakturering', icon: Receipt },
    seesOwnAlerts && {
      to: seesAllAlerts ? '/alarmer?recipient=all' : '/alarmer',
      label: 'Alarmer',
      icon: ShieldAlert,
      badge: openCount(seesAllAlerts ? allAlerts : myAlerts),
    },
    can('customers.view') && { to: '/kunder', label: 'Kunder', icon: Users },
    (user?.reports.length ?? 0) > 0 && { to: '/rapporter', label: 'Rapporter', icon: ListChecks },
    can('budget.view') && { to: '/budget', label: 'Budget', icon: PiggyBank },
    /* Blot én af rettighederne under /admin - siden viser saa kun de kort, rollen kan aabne. */
    canAny(...ADMIN_PERMISSIONS) && { to: '/admin', label: 'Administration', icon: Settings },
  ].filter((item): item is NavItem => Boolean(item));

  return (
    <div className="flex min-h-screen">
      {/*
        Below `md` the sidebar is hidden, and the menu is the drawer further down: a 60 px rail
        next to a 375 px page left too little room for the tables.
      */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 flex-col border-r border-line bg-surface transition-all md:flex',
          sidebarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        )}
      >
        {/*
          `px-3.5` (14 px): maerket er 28 px bredt, saa dets midte lander paa 28 px - samme
          lodrette linje som ikonernes midte nedenfor (20 + 8). De to staar dermed i én
          kolonne, ogsaa selvom maerket er stoerre end ikonerne.
        */}
        <div className="flex h-header items-center gap-2 border-b border-line px-3.5">
          <CompassMark />
          {!sidebarCollapsed ? <span className="text-sm font-semibold">Codepass</span> : null}
        </div>

        <SidebarNav items={items} collapsed={sidebarCollapsed} />

        <button
          type="button"
          onClick={toggleSidebar}
          className="flex h-10 items-center justify-center border-t border-line text-ink-muted hover:bg-canvas"
          aria-label={sidebarCollapsed ? 'Udvid menu' : 'Skjul menu'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </aside>

      {/* The phone menu: the same links, always with their names. z-50 like a modal. */}
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-ink/40"
            aria-label="Luk menu"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside
            className="relative flex h-full w-sidebar max-w-[85vw] flex-col bg-surface shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="flex h-header items-center gap-2 border-b border-line px-3.5">
              <CompassMark />
              <span className="flex-1 text-sm font-semibold">Codepass</span>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Luk menu"
                onClick={() => setMobileNavOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SidebarNav
              items={items}
              collapsed={false}
              onNavigate={() => setMobileNavOpen(false)}
            />
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          Lagdelingen paa en side, nedefra og op: tabelraekker (0), tabelhoved (z-10),
          maerkatlinjen ved massehandlinger (z-20), denne header (z-30), dropdowns (z-40),
          modaler (z-50). Headeren skal ligge over alt hvad der ruller forbi under den.
        */}
        <header className="sticky top-0 z-30 flex h-header items-center justify-end gap-3 border-b border-line bg-surface px-3 md:px-5">
          <Button
            variant="ghost"
            size="icon"
            className="mr-auto md:hidden"
            aria-label="Åbn menu"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          {seesOwnAlerts ? (
            <NavLink
              to="/alarmer"
              className="relative rounded-md p-2 text-ink-muted hover:bg-canvas"
              aria-label={`Mine alarmer${myOpenAlerts > 0 ? `, ${myOpenAlerts} åbne` : ''}`}
              title="Mine alarmer"
            >
              <Bell className="h-4 w-4" />
              {myAlerts && myAlerts.critical > 0 ? (
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-danger" />
              ) : null}
            </NavLink>
          ) : null}

          <div className="flex items-center gap-3 border-l border-line pl-3">
            <NavLink to="/profil" className="min-w-0 text-right leading-tight hover:underline">
              <span className="block max-w-[10rem] truncate text-sm font-medium sm:max-w-none">
                {user?.name}
              </span>
              <span className="block text-xs text-ink-muted">{user?.role_label ?? ''}</span>
            </NavLink>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Log ud"
              onClick={() => {
                void logout().then(() => navigate('/login'));
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-content flex-1 p-4 md:p-6">
          <Outlet />
        </main>

        {/* h-10 matcher sidepanelets kollaps-knap, saa de to flugter i hoejden nederst. */}
        <footer className="flex h-10 items-center border-t border-line px-4 text-xs text-ink-muted md:px-6">
          OneCode ApS — Codepass
        </footer>
      </div>
    </div>
  );
}

/** The menu's links - in the sidebar, and in the drawer on a phone. */
function SidebarNav({
  items,
  collapsed,
  onNavigate,
}: {
  items: readonly NavItem[];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    /*
      `overflow-x-hidden` er ikke pynt. `overflow-y-auto` alene efterlader overflow-x
      som `visible`, og CSS regner den da om til `auto` - nav'en bliver altsaa ogsaa en
      vandret rulleflade. Sker der bare én pixels overloeb i den sammenfoldede skinne,
      dukker der en vandret rullebjaelke op, og saa kan raekken af ikoner skubbe sig
      sidelaens. Skjult vandret overloeb kan den ikke.
    */
    <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          /*
           * Samme vandrette indrykning i begge tilstande, saa ikonet ikke flytter sig
           * naar panelet foldes sammen. Ingen `justify-center`: den centrerede ikonet
           * i et felt der skifter bredde, og flyttede det 6 px.
           *
           * `px-3` og ikke mere: skinnen er 60 px, men `border-r` spiser 1 px af dem,
           * saa der er 59 px indhold. Nav'ens p-2 tager 16 og linkets px-3 tager 24,
           * hvilket lader 19 px staa til et ikon paa 16. Med px-3.5 blev der kun 15 px
           * tilbage, og ikonet - der har shrink-0 - stak en pixel udenfor.
           *
           * Ikonet begynder dermed paa 20 px i begge tilstande, og dets midte paa 28
           * flugter med maerket i toppen.
           *
           * `h-9` (36 px) og ikke `py-2`: raekkens hoejde skal vaere den samme i begge
           * tilstande, og med lodret luft alene var den det ikke. Udfoldet saetter
           * teksten hoejden - `text-sm` har en linjehoejde paa 20 px - saa raekken blev
           * 8 + 20 + 8 = 36 px. Sammenfoldet er der kun ikonet paa 16 px, og raekken
           * blev 32. De 4 px lagde sig oven i hinanden ned gennem menuen, saa det
           * nederste ikon sprang 28 px, hver gang panelet blev foldet ud eller sammen.
           * En fast hoejde goer raekken uafhaengig af om teksten er der.
           */
          className={({ isActive }) =>
            cn(
              'mb-1 flex h-9 items-center gap-2 rounded-md px-3 text-sm',
              isActive ? 'bg-brand-50 font-medium text-brand-800' : 'text-ink hover:bg-canvas',
            )
          }
          title={collapsed ? item.label : undefined}
        >
          <item.icon className="h-4 w-4 shrink-0" aria-hidden />
          {!collapsed ? <span className="flex-1">{item.label}</span> : null}
          {!collapsed && item.badge ? (
            <span className="rounded-full bg-danger px-1.5 text-xs font-semibold text-white">
              {item.badge}
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}

/** Codepass-maerket: en stiliseret kompasnaal i en cirkel. PRD par. 15.6. */
function CompassMark() {
  return (
    <svg viewBox="0 0 32 32" className="h-7 w-7 shrink-0" aria-hidden>
      <circle cx="16" cy="16" r="14" fill="none" stroke="#1E40AF" strokeWidth="2.5" />
      <path d="M11 21l4.2-8.4L20 11l-2 8.4z" fill="#1E40AF" />
    </svg>
  );
}
